import { useMemo, useState } from 'react';
import { useFormikContext } from 'formik';
import {
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Autocomplete,
} from '@mui/material';
import { EmptyState, MediaContent, SortableList } from '@/components';
import { Content } from '@/types';
import * as Yup from 'yup';
import { useAppSelector } from '@/hooks';
import { useParams } from 'react-router-dom';
import { organizationContent } from '@/features/organizations/slice';
import { HugeiconsIcon } from '@hugeicons/react';
import { Search01Icon } from '@hugeicons/core-free-icons';
import { IMAGE_PLACEHOLDER } from '@/constants/global';

export const reqElements = Yup.string()
  .required('Necesitas llenar este campo')
  .min(3, 'El ingrediente debe tener un mínimo de 3 caracteres');

interface InputProps {
  value: string;
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
}

export const Input = ({ value, onBlur }: InputProps) => {
  const [inputValue, setInputValue] = useState(value);

  const handleChange = (e: { target: { value: string } }) => {
    setInputValue(e.target.value);
  };

  return (
    <TextField
      value={inputValue}
      onChange={handleChange}
      onBlur={onBlur}
      fullWidth={true}
      sx={{ backgroundColor: 'white' }}
    />
  );
};

const Extras = () => {
  const { organizationId } = useParams<{ organizationId: string; }>()
  const { values, setFieldValue } = useFormikContext<Content>();

  const content = useAppSelector(organizationContent(organizationId)) as Content[];
  
  const selectedContent = useMemo(() => {
    if (values?.extras) {
      return values?.extras?.map(extraId => {
        return content.find(({ id }) => id === extraId);
      }) as unknown as Content[]
    }
    return [] as Content[];
  }, [content, values?.extras])

  const selectedOptions = useMemo(() => {
    return selectedContent.map((o) => ({ id: o.id, label: o.title }))
  }, [selectedContent])
  
  const options = useMemo(() => {
    return content.map((o) => ({ id: o.id, label: o.title }))
  }, [content])

  const onChange = (options: Content[]) => {
    setFieldValue('extras', options.map(({ id }) => id));
  };

  const onDelete = (options: Content[]) => {
    setFieldValue('extras', options.map(({ id }) => id));
  };

  return (
    <Paper
      elevation={2}
      sx={{
        padding: 2,
        borderRadius: 2,
      }}
    >
      <Stack spacing={3}>
        <Autocomplete
          disablePortal
          options={options}
          renderInput={(params) => (
            <TextField 
              {...params} 
              InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start" sx={{ paddingLeft: 1 }}>
                  <HugeiconsIcon icon={Search01Icon} />
                </InputAdornment>
              ),
            }}
            />
          )}
          onChange={((_, option) => {
            const draft = values?.extras || [];

            if (option?.id && !draft.includes(option.id)) {
              setFieldValue('extras', [...draft, option.id]);
            }
          })}
        />
        {selectedOptions.length ? (
          <SortableList
            items={selectedOptions}
            onChange={onChange}
            onDelete={onDelete}
            renderItem={(option, index) => (
              <MediaContent
                src={selectedContent[index]?.images?.[0] || IMAGE_PLACEHOLDER}
                title={option.label}
              />
            )}
          />
        ) : (
          <Stack padding={4}>
            <EmptyState
              title="Platos Extras"
              description="Elige los platos que desees agregar usando el campo de búsqueda"
            />
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

export default Extras;
